import { Button, Checkbox, Collapse, Form, Input, Modal, ModalProps, Select, Spin, notification } from 'antd';
import { IAdminInfo, IEditAdmin } from 'types/administration.types';
import { NAME_REGEX, PASSWORD_REGEX } from 'pages/Administration/constants';
import { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { getFormErrorFields } from 'utils/errors';
import isEqual from 'lodash/isEqual';
import { roleOptions } from 'constants/roles';

type EditAdministrationModalProps = {
  onHideModal: () => void;
  onEdit: (data: IEditAdmin) => Promise<any>;
  initialValues: IAdminInfo;
  onSuccess?: () => void;
} & ModalProps;

export const EditAdministrationModal = ({
  onHideModal,
  onEdit,
  initialValues,
  onSuccess,
  ...modalProps
}: EditAdministrationModalProps) => {
  const [form] = Form.useForm<IEditAdmin>();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [disabledButtons, setDisabledButtons] = useState(false);

  useEffect(() => {
    form.resetFields();
    setDisabledButtons(true);
  }, [form, initialValues]);
  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async (data: IEditAdmin) => {
    try {
      setLoading(true);
      await onEdit({ ...data, id: initialValues.id });
      onSuccess && onSuccess();
      onHideModal();
      setNewPassword(false);
      setDisabledButtons(true);
    } catch (e: any) {
      if (e && e.violations) {
        const errorFields = getFormErrorFields(e.violations, {});
        form.setFields(errorFields);
      } else {
        notification.error(e);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      onCancel={onHideModal}
      {...modalProps}
      forceRender={true}
      footer={[
        <Button key="create" type="primary" onClick={handleSubmit} loading={loading} disabled={disabledButtons}>
          Изменить
        </Button>,
      ]}
    >
      <Form
        name="editAdmin"
        form={form}
        onFinish={handleFinish}
        autoComplete="off"
        layout="vertical"
        labelWrap
        initialValues={initialValues}
        preserve={false}
        scrollToFirstError={true}
        onValuesChange={(_, values) => {
          setDisabledButtons(isEqual(values, initialValues));
        }}
      >
        <Spin spinning={loading} size="default" indicator={<LoadingOutlined spin />}>
          <Form.Item
            name="email"
            label="Логин (e-mail)"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                type: 'email',
                message: 'Неверный формат почты',
              },
            ]}
          >
            <Input placeholder="Введите e-mail" />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Фамилия"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должна включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите фамилию" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Имя"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должно включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите имя" />
          </Form.Item>
          <Form.Item
            name="patronymic"
            label="Отчество"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должно включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите отчество" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Роль"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
            ]}
          >
            <Select disabled={true} placeholder="Выберите роль" options={roleOptions} />
          </Form.Item>
          <Collapse onChange={(key) => setNewPassword(!!key)} accordion destroyInactivePanel={true}>
            <Collapse.Panel
              showArrow={false}
              key="password"
              header={
                <Checkbox
                  checked={newPassword}
                  style={{
                    pointerEvents: 'none',
                  }}
                >
                  Установить новый пароль
                </Checkbox>
              }
            >
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: 'Обязательное поле',
                  },
                  {
                    min: 6,
                    max: 30,
                    pattern: PASSWORD_REGEX,
                    // eslint-disable-next-line no-template-curly-in-string
                    message:
                      'Пароль должен включать не менее ${min} и не более ${max} символов, содержать только латинские буквы, цифры и специальные символы',
                  },
                ]}
              >
                <Input.Password placeholder="Введите новый пароль" />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </Spin>
      </Form>
    </Modal>
  );
};
